import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { API_BASE_URL } from '@app/core/tokens/api-base-url.token';
import { SnackbarService } from '@app/core/services/snackbar.service';
import {
  CreateSeriesRequest,
  SeriesFilters,
  SeriesModel,
  SeriesScope,
  UpdateSeriesRequest,
} from '@app/core/models/product-parameters/series.model';

@Injectable({
  providedIn: 'root',
})
export class SeriesService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);

  readonly isLoading = signal(false);

  private get baseUrl(): string {
    return `${this.apiBaseUrl}/b2b/series`;
  }

  getAll(filters: SeriesFilters = {}): Observable<SeriesModel[]> {
    this.isLoading.set(true);

    let params = new HttpParams();
    params = this.appendParam(params, 'networkId', filters.networkId);
    for (const categoryId of filters.categoryIds ?? []) {
      const value = categoryId.trim();
      if (value) params = params.append('categoryIds', value);
    }
    if (filters.active !== null && filters.active !== undefined) {
      params = params.set('active', String(filters.active));
    }

    return this.http.get<SeriesModel[]>(this.baseUrl, { params }).pipe(
      catchError(error => {
        console.error('Ошибка при получении планов:', error);
        this.translate.get('Ошибка при получении планов').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        return of([]);
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  /**
   * GET /b2b/series/by-network/{networkId} | /by-partner/{partnerId} | /all
   */
  getByScope(scope: SeriesScope = {}): Observable<SeriesModel[]> {
    this.isLoading.set(true);

    const networkId = scope.networkId?.trim();
    const partnerId = scope.partnerId?.trim();
    const url = networkId
      ? `${this.baseUrl}/by-network/${encodeURIComponent(networkId)}`
      : partnerId
        ? `${this.baseUrl}/by-partner/${encodeURIComponent(partnerId)}`
        : `${this.baseUrl}/all`;

    return this.http.get<SeriesModel[]>(url).pipe(
      catchError(error => {
        console.error('Ошибка при получении планов:', error);
        this.translate.get('Ошибка при получении планов').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        return of([]);
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  private appendParam(params: HttpParams, key: string, value?: string): HttpParams {
    const normalized = value?.trim();
    return normalized ? params.set(key, normalized) : params;
  }

  create(body: CreateSeriesRequest): Observable<SeriesModel> {
    this.isLoading.set(true);
    return this.http.post<SeriesModel>(this.baseUrl, body).pipe(
      catchError(error => {
        console.error('Ошибка при создании плана:', error);
        this.translate.get('Ошибка при создании плана').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  update(id: string, body: UpdateSeriesRequest): Observable<SeriesModel> {
    this.isLoading.set(true);
    return this.http.put<SeriesModel>(`${this.baseUrl}/${id}`, body).pipe(
      catchError(error => {
        console.error('Ошибка при обновлении плана:', error);
        this.translate.get('Ошибка при обновлении плана').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  delete(id: string): Observable<void> {
    this.isLoading.set(true);
    return this.http.delete<void>(`${this.baseUrl}/${id}`).pipe(
      catchError(error => {
        console.error('Ошибка при удалении плана:', error);
        this.translate.get('Ошибка при удалении плана').subscribe(msg => {
          this.snackbarService.error(msg);
        });
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }
}
